import { getHumanizedFileSize2, getMaxFileSize } from '@ingenico/react-ui/file-handlerV2';
import { Level, useShowNotification } from '@ingenico/react-notifications';
import { useBusinessService, useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { CoreError } from '@ingenico/api-sdk-core/common';
import { useAtom } from 'jotai';
import { toString } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { firstValueFrom, from } from 'rxjs';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useDeviceManagementError } from '../../hooks';
import { artifactComponentTypeEnum, artifactTypeEnum } from '../../domain/models/artifact.model';
import { RESOURCE_ARTIFACT_DEFAULT_CAPABILITIES } from '../../domain/models/resources.model';
import { openCreateResourceModalAtom } from './CreateResourceModal.atom';
import { useCreateResourceModalForm, type CreateResourceFormValues } from './useCreateResourceModalForm.hook';

interface UploadedArtifactState {
  readonly artifactId: string;
  readonly fileHash: string;
}

interface UseCreateResourceModalVMInput {
  readonly onCreated?: () => void;
}

export interface UseCreateResourceModalVMOutput {
  readonly form: ReturnType<typeof useCreateResourceModalForm>;
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly hasSubmitError: boolean;
  readonly submitErrorMessage: string;
  readonly isUploadingFile: boolean;
  readonly isCapabilitiesLoading: boolean;
  readonly capabilitiesError: boolean;
  readonly fileExtensions: string[];
  readonly maxFileSize: number;
  readonly unitFileSize: 'Bytes' | 'KB' | 'MB' | 'GB';
  readonly displayedMaxFileSize: string;
  readonly selectedFileError?: string;
  readonly createButtonDisabled: boolean;
  readonly onClose: () => void;
  readonly handleCreate: () => void;
  readonly handleUploadingProgress: (isUploading?: boolean) => void;
  readonly handleFilesChange: (
    uploadedFiles: CreateResourceFormValues['files'],
    onChange: (files: CreateResourceFormValues['files']) => void,
  ) => void;
  readonly handleRemoveSelectedFile: () => void;
  readonly handleFileDialogCancel: () => void;
}

export function useCreateResourceModalVM({ onCreated }: UseCreateResourceModalVMInput): UseCreateResourceModalVMOutput {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.RESOURCES, ALL_LNGS_NAMESPACES.COMMON]);
  const showNotification = useShowNotification();
  const deviceManagementService = useBusinessService(DeviceManagementService);
  const [isOpen, setIsOpen] = useAtom(openCreateResourceModalAtom);
  const form = useCreateResourceModalForm();
  const { reset, handleSubmit, setValue, getValues } = form;

  const { getErrorMessage } = useDeviceManagementError();

  const [uploadedArtifact, setUploadedArtifact] = useState<UploadedArtifactState | null>(null);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>('');
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [createResourceState, setCreateResourceState] = useAsyncDataState<void, CoreError>();

  const hasSubmitError = !!submitErrorMessage;

  const {
    data: resourceCapabilities,
    isLoading: isCapabilitiesLoading,
    isError: capabilitiesError,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (service) => {
        if (!isOpen) {
          return null;
        }

        return service.getResourceArtifactCapabilities();
      },
      [isOpen],
    ),
  );

  const { fileExtensions, maxFileSize, unitFileSize } = resourceCapabilities ?? RESOURCE_ARTIFACT_DEFAULT_CAPABILITIES;

  const resetFormState = useCallback(() => {
    reset();
    setUploadedArtifact(null);
    setSubmitErrorMessage('');
    setIsUploadingFile(false);
  }, [reset]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    resetFormState();
  }, [resetFormState, setIsOpen]);

  const selectedFileError = form.watch('files')?.[0]?.error;
  const hasSelectedFileError = !!selectedFileError;

  const handleUploadingProgress = useCallback((isUploading?: boolean) => {
    setIsUploadingFile(!!isUploading);
  }, []);

  const handleRemoveSelectedFile = useCallback(() => {
    setValue('files', [], { shouldTouch: true, shouldValidate: true });
    setUploadedArtifact(null);
    setIsUploadingFile(false);
  }, [setValue]);

  const handleFileDialogCancel = useCallback(() => {
    if (getValues('files')?.length) {
      return;
    }
    setValue('files', [], { shouldTouch: true, shouldValidate: true });
  }, [getValues, setValue]);

  const handleFilesChange = useCallback(
    (
      uploadedFiles: CreateResourceFormValues['files'],
      onChange: (files: CreateResourceFormValues['files']) => void,
    ) => {
      const [firstFile] = uploadedFiles;
      onChange(firstFile ? [firstFile] : []);
      setUploadedArtifact((current) => (current?.fileHash === firstFile?.hash ? current : null));
    },
    [],
  );

  const convertFileForRequest = useCallback(
    async (file: File) => new Blob([new Uint8Array(await file.arrayBuffer())], { type: 'application/octet-stream' }),
    [],
  );

  const submitCreateResource = useCallback(
    async (values: CreateResourceFormValues): Promise<void> => {
      const resourceFile = values.files[0];
      if (!resourceFile || !!resourceFile.error || !resourceFile.file) {
        throw new Error('Invalid selected file.');
      }

      let artifactId = uploadedArtifact?.artifactId;

      if (!artifactId || uploadedArtifact?.fileHash !== resourceFile.hash) {
        const blobFile = await convertFileForRequest(resourceFile.file);
        const artifactResponse = await firstValueFrom(
          deviceManagementService.createArtifact({
            artifactName: resourceFile.name,
            checksum: toString(resourceFile.hash),
            componentType: artifactComponentTypeEnum.RESOURCE,
            type: artifactTypeEnum.DATA_FILE,
            file: blobFile,
          }),
        );

        artifactId = artifactResponse.id;
        setUploadedArtifact({ artifactId, fileHash: resourceFile.hash });
      }

      const trimmedDescription = values.description?.trim();
      const createResourceBody: Parameters<DeviceManagementService['createResource']>[0] = {
        name: values.name.trim(),
        artifactId: toString(artifactId),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      };

      await firstValueFrom(deviceManagementService.createResource(createResourceBody));
    },
    [convertFileForRequest, deviceManagementService, uploadedArtifact],
  );

  const onSubmit = useCallback(
    (values: CreateResourceFormValues): void => {
      requestAndUpdateState(from(submitCreateResource(values)), setCreateResourceState, {
        onSuccess: () => {
          showNotification({
            level: Level.Success,
            content: t('success.createResource', { ns: ALL_LNGS_NAMESPACES.RESOURCES }),
          });
          onClose();
          onCreated?.();
        },
        onError: (error) => {
          setSubmitErrorMessage(getErrorMessage(error));
        },
      });
    },
    [getErrorMessage, onClose, onCreated, setCreateResourceState, showNotification, submitCreateResource, t],
  );

  const handleCreate = useCallback(() => {
    void handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const createButtonDisabled = useMemo(
    () => createResourceState.isLoading || hasSelectedFileError || isCapabilitiesLoading || isUploadingFile,
    [createResourceState.isLoading, hasSelectedFileError, isCapabilitiesLoading, isUploadingFile],
  );

  return {
    form,
    isOpen,
    isSubmitting: createResourceState.isLoading,
    hasSubmitError,
    submitErrorMessage,
    isUploadingFile,
    isCapabilitiesLoading,
    capabilitiesError,
    fileExtensions,
    maxFileSize,
    unitFileSize,
    displayedMaxFileSize: getHumanizedFileSize2(getMaxFileSize(maxFileSize, unitFileSize)),
    selectedFileError,
    createButtonDisabled,
    onClose,
    handleCreate,
    handleUploadingProgress,
    handleFilesChange,
    handleRemoveSelectedFile,
    handleFileDialogCancel,
  };
}
